import type {
    FUSportsPlayerCompetitor,
    FUSportsSquadMember,
    FUSportsTeamPlayerSeasonStats,
} from "fansunited-data-layer";

function statNumber(row: FUSportsTeamPlayerSeasonStats, key: string): number {
    const hit = row.statistics.find((s) => s.key === key);
    if (!hit || hit.value === null) return 0;
    const n = Number(hit.value);
    return Number.isFinite(n) ? n : 0;
}

function sumKey(rows: FUSportsTeamPlayerSeasonStats[], key: string): number {
    let total = 0;
    for (const row of rows) total += statNumber(row, key);
    return total;
}

/**
 * Project an AdaptedTeam.squad[] + athleteStats[] into FUSportsSquadMember[].
 *
 * The feed publishes one `athleteStats` row per (player, competition). We
 * sum across competitions so a GK with 11 total apps shows 11 (not the
 * lowest per-competition count). Rows are already filtered to current
 * active seasons by the adapter.
 *
 * Field names map feed snake_case → canonical FUSportsPlayerSeasonStatistics
 * lowercase/camelCase — e.g. `clean_sheets` → `cleansheets`,
 * `goals_conceded` → `conceded`, `bench_appearances` → `substituteIn`.
 * Mismatches here silently zero stats in squad cards.
 */
export function toSquadMembers(
    players: FUSportsPlayerCompetitor[],
    athleteStats: FUSportsTeamPlayerSeasonStats[],
): FUSportsSquadMember[] {
    const byAthlete = new Map<string, FUSportsTeamPlayerSeasonStats[]>();
    for (const row of athleteStats) {
        if (!byAthlete.has(row.athleteId)) byAthlete.set(row.athleteId, []);
        byAthlete.get(row.athleteId)!.push(row);
    }

    return players.map((player) => {
        const rows = byAthlete.get(player.id) ?? [];
        const stats = rows.length > 0
            ? {
                  playerId: player.id,
                  seasonId: rows[0].seasonId,
                  appearances: sumKey(rows, "appearances"),
                  started: sumKey(rows, "first_lineup_appearances"),
                  substituteIn: sumKey(rows, "bench_appearances"),
                  minutes: sumKey(rows, "minutes"),
                  goals: sumKey(rows, "goals"),
                  penaltyGoals: sumKey(rows, "penalties_scored"),
                  ownGoals: sumKey(rows, "own_goals"),
                  assists: sumKey(rows, "assists"),
                  yellowCards: sumKey(rows, "yellow_cards"),
                  redCards: sumKey(rows, "red_cards"),
                  penaltiesMissed: sumKey(rows, "penalties_unsuccessful"),
                  cleansheets: sumKey(rows, "clean_sheets"),
                  conceded: sumKey(rows, "goals_conceded"),
              }
            : undefined;

        const shirt = player.shirtNumber ? Number(player.shirtNumber) : undefined;

        return {
            id: player.id,
            name: player.name,
            slug: player.slug,
            position: player.position,
            shirtNumber: Number.isFinite(shirt) ? shirt : undefined,
            country: player.country,
            gender: player.gender,
            birthdate: player.birthdate,
            profile: player.profile,
            assets: player.assets,
            seasonStatistics: stats,
        } as FUSportsSquadMember;
    });
}
